import algoliasearch, { SearchIndex } from 'algoliasearch/lite';
import { useCallback, useEffect, useMemo, useState } from 'react';

import * as graphql from '@treams/treams-network';

import { useViewer } from './useViewer';
import { useViewerRoles } from './useViewerRoles';

type SearchResult = {
	objectID: string;
	id: string;
	firstName: string;
	lastName: string;
	username: string;
	email: string;
	avatarID: string;
	groupID: string;
	avatar: { source: { url: string } } | undefined;
	lastLoginAt: number;
	activatedAt: number;
};

export function useSearch(
	query: string,
	options?: {
		filters?: string;
		limit?: number;
		skip?: boolean;
		excludeViewer?: boolean;
		status?: graphql.UserStatus[];
	},
) {
	const { filters, limit, skip, excludeViewer, status } = options ?? {};

	// cache-first (instead of the app-wide cache-and-network default) so a
	// batch whose assets are all cached resolves without a network request;
	// combined with the Query.assets cache redirect this fetches each asset at
	// most once per browser session. Refetching would also churn the cache:
	// every response carries freshly signed (but equivalent) asset URLs.
	const [getAssets] = graphql.useAssetsLazyQuery({ fetchPolicy: 'cache-first' });
	const viewer = useViewer();
	const roles = useViewerRoles();
	const [getKey] = graphql.useSearchKeyLazyQuery({ fetchPolicy: 'network-only' });

	const [version, setVersion] = useState(0);
	const [loading, setLoading] = useState(!skip);
	const [expiresAt, setExpiresAt] = useState(new Date());
	const [results, setResults] = useState<SearchResult[]>([]);
	const [index, setIndex] = useState<SearchIndex | null>(null);

	const getIndex = useCallback(() => {
		getKey().then(({ data }) => {
			if (!data) return;

			setExpiresAt(new Date(data.searchKey.expiresAt));

			setIndex(algoliasearch(data.searchKey.appID, data.searchKey.token).initIndex('users'));
		});
	}, [getKey]);

	const canViewUsername = useMemo(() => {
		return roles.isWorkspaceSuperuser || roles.isWorkspaceAdmin || roles.isEngagementOfficer;
	}, [roles.isWorkspaceSuperuser, roles.isWorkspaceAdmin, roles.isEngagementOfficer]);

	const statusOptions = useMemo(() => status?.join(',') ?? '', [status]);

	// Depend on the stable user ID, not the viewer object: the viewer's
	// identity changes on every Apollo cache broadcast (e.g. when the avatar
	// Assets query writes a fresh signed URL for the viewer's own avatar),
	// which would restart the search and loop forever.
	const viewerID = viewer?.id;

	useEffect(() => {
		let stop = false;

		if (skip) return;

		setResults([]);

		setLoading(true);

		setVersion((v) => v + 1);

		if (!index || expiresAt <= new Date()) return getIndex();

		const timeout = setTimeout(async () => {
			let page = 0;
			let total = 0;

			const now = Date.now();
			const conditions: string[] = [];
			const restrictSearchableAttributes = ['firstName', 'lastName'];

			const attributesToRetrieve = [
				'objectID',
				'firstName',
				'lastName',
				'email',
				'avatarID',
				'groupID',
				'lastLoginAt',
				'activatedAt',
			];

			/**
			 * We only allow searching on username if the viewer is a superuser.
			 * This is due to usernames matching externalReferenceID's, which can be
			 * sensitive information.
			 */
			if (canViewUsername) {
				attributesToRetrieve.push('username');
				restrictSearchableAttributes.push('username');
				attributesToRetrieve.push('email');
				restrictSearchableAttributes.push('email');
			}

			const statuses = new Set(statusOptions ? statusOptions.split(',') : []);

			if (statuses.size === 3) {
				// Don't filter at all.
			} else if (statuses.size === 2) {
				if (!statuses.has(graphql.UserStatus.Active)) {
					conditions.push(
						'(' + [`activatedAt > ${now + 1000 * 60 * 5}`, 'isArchived:true'].join(' OR ') + ')',
					);
				}

				if (!statuses.has(graphql.UserStatus.Inactive)) {
					conditions.push(
						'(' + [`activatedAt <= ${now + 1000 * 60 * 5}`, 'isArchived:true'].join(' OR ') + ')',
					);
				}

				if (!statuses.has(graphql.UserStatus.Archived)) {
					conditions.push('isArchived:false');
				}
			} else if (statuses.size === 1) {
				if (statuses.has(graphql.UserStatus.Active)) {
					conditions.push(
						`isArchived = 0 AND activatedAt <= ${
							now + 1000 * 60 * 5
						} AND (deactivatedAt = 0 OR deactivatedAt > ${now + 1000 * 60 * 5})`,
					);
				} else if (statuses.has(graphql.UserStatus.Inactive)) {
					conditions.push(`isArchived = 0 AND activatedAt > ${now + 1000 * 60 * 5}`);
				} else if (statuses.has(graphql.UserStatus.Archived)) {
					conditions.push(
						`(isArchived = 1 OR deactivatedAt < ${
							now + 1000 * 60 * 5
						}) AND (isArchived = 1 OR deactivatedAt != 0)`,
					);
				}
			}

			while (!stop) {
				const response = await index.search<SearchResult>(query, {
					filters: conditions.concat(filters ? [filters] : []).join(' AND '),
					page,
					hitsPerPage: limit ?? 10_000,
					attributesToRetrieve,
					restrictSearchableAttributes,
				});

				if (stop) break;

				const reset = total === 0;

				page += 1;

				total += response.hits.length;

				setResults((results) => {
					const update = response.hits.reduce(
						(update, hit) => {
							if (typeof limit === 'number' && update.length >= limit) return update;

							const id = hit.objectID.split('/').pop()!;

							if (excludeViewer && id === viewerID) return update;

							update.push({
								id,
								objectID: hit.objectID,
								firstName: hit.firstName,
								lastName: hit.lastName,
								username: hit.username,
								email: hit.email,
								avatarID: hit.avatarID,
								groupID: hit.groupID,
								avatar: undefined,
								activatedAt: hit.activatedAt,
								lastLoginAt: hit.lastLoginAt,
							});

							return update;
						},
						reset ? [] : results,
					);

					return update;
				});

				if (page >= response.nbPages || (typeof limit === 'number' && total >= limit)) {
					break;
				}
			}

			if (!stop) setLoading(false);
		}, 200);

		return () => {
			stop = true;

			clearTimeout(timeout);
		};
	}, [
		getIndex,
		index,
		expiresAt,
		query,
		filters,
		limit,
		skip,
		excludeViewer,
		viewerID,
		canViewUsername,
		statusOptions,
	]);

	useEffect(() => {
		// Note: the assets(ids:) resolver filters out IDs that no longer exist
		// (e.g. deleted avatars still referenced by the search index), so a
		// stale avatarID degrades to initials instead of erroring the whole
		// batch. Unresolvable IDs are intentionally NOT remembered here, so
		// they are re-requested when `results` changes (e.g. per Algolia page
		// or new search). This is a deliberate trade-off: it keeps this effect
		// free of long-lived state that could go stale (previously a cause of
		// avatars being hidden forever) and lets transient failures self-heal.
		// It cannot loop: an all-miss response leaves `results` referentially
		// unchanged (see below), so the effect does not retrigger itself, and
		// Apollo caches repeated identical batches.
		const assetIDs = results
			.filter((result) => result.avatarID && !result.avatar)
			.map((result) => result.avatarID);

		if (assetIDs.length === 0) return;

		let stop = false;

		getAssets({ variables: { ids: assetIDs } }).then(({ data }) => {
			if (stop || !data) return;

			setResults((results) => {
				let changed = false;

				const next = results.map((result) => {
					if (result.avatar || !result.avatarID) return result;

					const avatar = data.assets.find((asset) => asset.id === result.avatarID);

					if (!avatar) return result;

					changed = true;

					return { ...result, avatar };
				});

				// Keep the same reference when nothing was filled in (e.g. all
				// remaining avatarIDs point to deleted assets), so this effect
				// (which depends on `results`) doesn't retrigger itself.
				return changed ? next : results;
			});
		});

		return () => {
			stop = true;
		};
	}, [results, getAssets]);

	return { loading, results, version };
}
